<template>
    <div class="calc-dropdown" v-bind="$attrs">
        <Select
            :class="['calc-dropdown__select', { 'calc-dropdown__select--invalid': invalid }]"
            :modelValue="computedValue"
            :options="getOptions"
            :optionLabel="optionLabel"
            :placeholder="translatedPlaceholder"
            :pt="{ overlay: { class: 'calc-select-overlay' } }"
            @update:modelValue="updateValue"
        >
            <template v-if="$slots.value" #value="slotProps">
                <slot name="value" v-bind="slotProps" />
            </template>

            <template v-if="$slots.option" #option="slotProps">
                <slot name="option" v-bind="slotProps" />
            </template>

            <template #dropdownicon>
                <CalcIcon name="caret" />
            </template>
        </Select>
    </div>
</template>

<script>
import Select from 'primevue/select'
import CalcIcon from './CalcIcon.vue'

export default {
    name: 'CalcDropdown',

    components: { Select, CalcIcon },

    inheritAttrs: false,

    props: {
        modelValue: {
            type: [String, Number, Object],
            default: '',
        },
        options: {
            type: Array,
            default: () => [],
        },
        optionLabel: {
            type: String,
            default: 'label',
        },
        optionValue: {
            type: String,
            default: 'value',
        },
        placeholder: {
            type: String,
            default: '',
        },
        invalid: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['update:modelValue'],

    computed: {
        getOptions() {
            return this.options.map((option) => ({
                value: option[this.optionValue] !== undefined ? option[this.optionValue] : option.value,
                label: this.translateLabel(option[this.optionLabel] !== undefined ? option[this.optionLabel] : option.label),
            }))
        },

        computedValue() {
            const selected = this.getOptions.filter((option) => option.value === this.modelValue)
            return selected.length === 1 ? selected[0] : ''
        },

        translatedPlaceholder() {
            if (!this.placeholder) return ''
            if (this.placeholder.startsWith('§')) return this.$t(this.placeholder)
            return this.placeholder
        },
    },

    methods: {
        translateLabel(label) {
            if (typeof label === 'string' && label.startsWith('§')) {
                return this.$t(label)
            }
            return label
        },

        updateValue(selected) {
            const next = selected && typeof selected === 'object' ? selected.value : selected
            this.$emit('update:modelValue', next)
        },
    },
}
</script>

<style scoped>
.calc-dropdown {
    position: relative;
    width: 100%;
}

.calc-dropdown :deep(.calc-dropdown__select),
.calc-dropdown .calc-dropdown__select {
    width: 100%;
    height: 48px;
    border-radius: 8px;
    border: 1px solid var(--calc-line);
    background: white;
    font-family: 'TWK Lausanne', 'Inter', sans-serif;
    font-size: 18px;
    line-height: 16px;
    letter-spacing: -0.02em;
    color: var(--calc-ink);
    outline: none;
    padding-left: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition:
        border-color var(--calc-t-fast),
        box-shadow var(--calc-t-fast);
}

.calc-dropdown :deep(.calc-dropdown__select.p-select):hover,
.calc-dropdown .calc-dropdown__select.p-select:hover {
    border-color: var(--calc-line);
}

.calc-dropdown :deep(.calc-dropdown__select.p-select.p-focus),
.calc-dropdown .calc-dropdown__select.p-select.p-focus {
    border-color: var(--calc-line);
    box-shadow: 0 0 0 1px var(--calc-line);
    outline: none;
}

.calc-dropdown :deep(.calc-dropdown__select--invalid),
.calc-dropdown .calc-dropdown__select--invalid {
    border-color: var(--calc-error, #d23030);
}

.calc-dropdown :deep(.calc-dropdown__select--invalid.p-focus),
.calc-dropdown .calc-dropdown__select--invalid.p-focus {
    border-color: var(--calc-error, #d23030);
    box-shadow: 0 0 0 1px var(--calc-error, #d23030);
}

.calc-dropdown :deep(.calc-dropdown__select .p-select-dropdown),
.calc-dropdown .calc-dropdown__select .p-select-dropdown {
    color: var(--calc-ink);
    width: 2.5rem;
}
</style>
